import type { KAPLAYCtx } from '../types.ts'
import type { RoomOptions } from '../types.ts'
import { useLang } from '../providers/i18n/i18n.ts'
import { useGameUiTextFrame } from '../objects/game-ui-text-frame.ts'
import { getTiledSize, parsePolygon, getFileCacheStr } from '../utils.ts'

export function useMeetingRoomC(k: KAPLAYCtx) {
  const cacheStr = getFileCacheStr()
  k.loadSprite('meetingRoomC', `sprites/rooms/meeting-room-c.png?${cacheStr}`)
  k.loadSprite('meetingRoomCHover', `sprites/rooms/meeting-room-c--hover.png?${cacheStr}`)
  const collidersJson = k.loadJSON(
    'meetingRoomCColliders',
    `colliders/meeting-room-c.parsed.json?${cacheStr}`,
  )

  const { showTextFrame } = useGameUiTextFrame(k)
  const { getLangString } = useLang(k)

  function setMeetingRoomC(roomOpts?: RoomOptions) {
    roomOpts = roomOpts || {}

    const roomObj = k.add()

    k.onLoad(() => {
      const { data } = collidersJson

      roomObj.add([
        k.sprite('meetingRoomC', {
          width: getTiledSize(data.width),
          height: getTiledSize(data.height),
        }),
        k.layer('background'),
        'meeting_room_c_background',
        'background',
      ])

      roomObj.add([
        k.sprite('meetingRoomCHover', {
          width: getTiledSize(data.width),
          height: getTiledSize(data.height),
        }),
        k.layer('hover'),
        'meeting_room_c_hover',
        'hover',
      ])

      data.objects.forEach((obj: any) => {
        const poly = parsePolygon(k, obj)
        roomObj.add([k.pos(), k.area({ shape: poly }), k.body({ isStatic: true }), 'colliders'])
      })

      roomObj.add([
        k.level(
          [
            '          ',
            '          ',
            '          ',
            '      ?   ',
            '          ',
            '          ',
            '        1 ',
            '          ',
            '          ',
          ],
          {
            tileWidth: getTiledSize(),
            tileHeight: getTiledSize(),
            tiles: {
              '1': () => [
                k.rect(getTiledSize(0.5), getTiledSize(), { fill: false }),
                k.pos(getTiledSize(0.5), 0),
                k.area({ isSensor: true }),
                'meeting_room_c_door_exit',
                'door_exit',
                'door',
              ],
              '?': () => [
                k.sprite('overlay', { anim: 'question' }),
                k.area({ isSensor: true }),
                k.layer('background'),
                'meeting_room_c_info',
                'sfx_emit_action',
                'info',
              ],
            },
          },
        ),
        k.layer('objects'),
        'meeting_room',
      ])

      // Set player position
      k.get('player')[0]?.setPos(k.vec2(getTiledSize(8), getTiledSize(6)))

      const doorExitCollision = k.onCollide('player', 'meeting_room_c_door_exit', () => {
        k.flash(k.BLACK, 0.5)
        roomObj.destroy()
        doorExitCollision.cancel()
        infoCollision.cancel()
        infoCollisionEnd.cancel()
        if (roomOpts.onExit) roomOpts.onExit()
      })

      const infoCollision = k.onCollide('player', 'meeting_room_c_info', () => {
        showTextFrame(getLangString('meeting_room_info'))
      })

      const infoCollisionEnd = k.onCollideEnd('player', 'meeting_room_c_info', () => {
        showTextFrame()
      })
    })

    return roomObj
  }

  return {
    setMeetingRoomC,
  }
}
